import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Checkbox,
  FormControl,
  Grid,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { Field, FieldProps, useFormikContext } from 'formik'
import { DatePicker } from 'formik-mui-x-date-pickers'
import { useConfirm } from 'material-ui-confirm'
import React, { FC, FormEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FormikVinciInput, VinciButton, VinciInput } from '~/components'
import CountrySelect from '~/components/CountrySelect/CountrySelect'
import { useResourceAutocomplete } from '~/components/ResourceAutocomplete/ResourceAutocomplete'
import { useAuth } from '~/hooks/useAuth'
import { useAircraftById } from '~/modules/aircraft/hooks'
import { CompanyForm } from '~/modules/company/components/CompanyForm'
import { createCompany, getCompanyContactInfo } from '~/modules/company/data/companyApi'
import { CreateAuditMenu } from '~/modules/inspection/components/InspectionForm/CreateAuditMenu'
import { checkIsSupervisorUp } from '~/modules/inspection/utils/checkIsSupervisorUp'
import { UserForm } from '~/modules/user/components'
import { useUserData } from '~/modules/user/data/useData'
import { createUser } from '~/modules/user/data/userApi'
import { companyAssetValidationSchema } from '~/tmp/assetTypes/components/CompanyAssetModal'
import { RealStateAssetForm } from '~/tmp/assetTypes/components/RealStateAssetForm'
import { RealStateAssetModal } from '~/tmp/assetTypes/components/RealStateAssetModal'
import { realStateExtraFields } from '~/tmp/assetTypes/extra_fields'
import { formatRealStateAddress } from '~/tmp/assetTypes/functions'
import { sortByAlphabeticalOrder } from '~/utils/comparators/sortByAlphabeticalOrder'
import { getContinentByCountryName } from '~/utils/countries'
import { removeEmptyValues } from '~/utils/removeEmptyValues'
import {
  IAircraft,
  IAircraftModel,
  ICompany,
  IContactInfo,
  IInspectionChecklist,
  initialAircraft,
  initialCompany,
  initialCustomAsset,
  initialUser,
  IRealStateAsset,
  IUser,
} from '~/utils/types'
import { ICustomAssetInspectionSubmitValues } from '../create-custom-asset-audit'
import { CustomField } from './CustomField'
import * as S from './RealStateAuditForm.styles'

interface IRealStateAuditFormProps {
  editing?: boolean
  onSave?: () => void
  onCreate?: () => void
}

export const RealStateAuditForm: FC<IRealStateAuditFormProps> = ({
  editing = false,
  onCreate,
  onSave,
}) => {
  const resourceAutocomplete = useResourceAutocomplete()
  const confirm = useConfirm()
  const { user } = useAuth()
  const { useListAllUsers } = useUserData()
  const users = useListAllUsers()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()
  const aircraftId = searchParams.get('aircraft_id')

  const formik = useFormikContext<ICustomAssetInspectionSubmitValues>()
  const setFieldValue = useMemo(() => formik.setFieldValue, [formik.setFieldValue])

  const { data: selectedAircraft } = useAircraftById(aircraftId ?? '')

  const [useSelectedAircraft, setUseSelectedAircraft] = useState(false)

  useEffect(() => {
    if (aircraftId && selectedAircraft) {
      setUseSelectedAircraft(true)
      setFieldValue('aircraft', selectedAircraft)
    }
  }, [aircraftId, selectedAircraft, setFieldValue])

  const handleSelectChecklist = (checklist: IInspectionChecklist | null) => {
    if (checklist) {
      formik.setFieldValue('general_info.checklist', checklist)
    } else {
      formik.setFieldValue('general_info.checklist', null)
    }
  }

  const handleSelectCustomer = (customer: ICompany | null) => {
    formik.setFieldValue('aircraft', useSelectedAircraft ? selectedAircraft : undefined)
    formik.setFieldValue('general_info.checklist', undefined)
    formik.setFieldValue('general_info.inspection_company', undefined)

    formik.setFieldValue('general_info.inspector', undefined)
    formik.setFieldValue('general_info.additional_inspectors', undefined)
    if (customer) {
      formik.setFieldValue('general_info.customer', customer)

      setCurrentCustomer(customer)
    } else {
      formik.setFieldValue('general_info.customer', null)
      formik.setFieldValue('general_info.checklist', null)
      setCurrentCustomer(null)
    }
  }

  const [currentCustomer, setCurrentCustomer] = useState<ICompany | null>(
    formik.values.general_info?.customer ?? null,
  )
  const submitButtonDisabled = useMemo(
    () => !formik.dirty || Object.keys(formik.errors).length > 0,
    [formik.errors, formik.dirty],
  )

  const sortedUsers = useMemo(() => {
    if (!users.data) return []

    return users.data.sort((a, b) => sortByAlphabeticalOrder(a.name, b.name))
  }, [users])

  const handleCreateUser = async (user: IUser) => {
    const values = removeEmptyValues(user) as IUser

    return await createUser(values)
  }

  const handleSave = () => {
    confirm({
      description: 'Are you sure you want to save this audit?',
    }).then(() => formik.submitForm())
  }

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const isCreateAuditMenuOpen = Boolean(anchorEl)

  const handleClose = () => {
    setAnchorEl(null)
  }

  interface ICustomAssetModalState {
    status: 'creating' | 'editing' | false
    values?: IAircraft
    initialAsCustom?: Partial<IRealStateAsset>
  }
  const [realStateAssetModal, setRealStateAssetModal] = useState<ICustomAssetModalState>({
    status: false,
    values: initialAircraft(),
  })

  const [contactInfoOptions, setContactInfoOptions] = useState<IContactInfo[]>(
    currentCustomer?.contact_info ?? [],
  )

  const sortedContactInfoOptions = useMemo(() => {
    return contactInfoOptions.sort((a, b) =>
      sortByAlphabeticalOrder(a.tech_focal ?? '', b.tech_focal ?? ''),
    )
  }, [contactInfoOptions])

  const [shouldRenderReloadContactInfo, setShouldRenderReloadContactInfo] = useState(false)

  const handleReloadContactInfo = async () => {
    if (!currentCustomer?._id) return

    const newContactInfo = await getCompanyContactInfo(currentCustomer._id)
    setContactInfoOptions(newContactInfo)
  }

  const theme = useTheme()

  return (
    <>
      <S.Section>
        <RealStateAssetModal
          creating={realStateAssetModal.status === 'creating'}
          open={realStateAssetModal.status !== false}
          values={realStateAssetModal.values}
          initialValuesAsCustom={realStateAssetModal.initialAsCustom}
          onMutate={(newItem) => {
            formik.setFieldValue('aircraft', newItem)
          }}
          onClose={() =>
            setRealStateAssetModal({
              status: false,
            })
          }
        />
        <S.InputGroup>
          <Field name='general_info.customer'>
            {(fieldProps: FieldProps) =>
              resourceAutocomplete<ICompany>({
                resourcePath: 'company',
                resourceFilter: (company) =>
                  company._id === (user?.company as ICompany)._id ||
                  company.company_type.includes('customer') ||
                  company.company_type.includes('asset_manager') ||
                  company.company_type.includes('inspection_company'),
                resourceSortComparator: (a, b) => sortByAlphabeticalOrder(a.name, b.name),
                requestProps: {
                  initialText: t('Creating customer...'),
                  errorText: t('There was an error creating customer'),
                  successText: t('Customer successfully created!'),
                  createRequestFunction: createCompany,
                },
                renderOption: (props, option) => (
                  <Box component='li' {...props} key={option._id}>
                    <Box>
                      <Typography>{option.name}</Typography>
                      <Typography variant='caption'>Parent: {option.parent ?? 'None'}</Typography>
                    </Box>
                  </Box>
                ),
                matchValueOnBlur(value, options) {
                  return options.find((i) => (value ? i.name.includes(value) : false))
                },
                onSelectResource: (company) => handleSelectCustomer(company),
                modalProps: {
                  form: {
                    initialValues: initialCompany(),
                  },
                  title: t('Create Company'),
                  children: <CompanyForm />,
                },
                autocompleteProps: {
                  sx: {
                    minWidth: 200,
                    my: 1,
                    flex: 1,
                  },
                  disabled: editing,
                  primaryIdentifier: 'name',
                  label: `${t('Ordered by')} *`,
                  ...fieldProps,
                },
              })
            }
          </Field>
          <Field name='general_info.inspection_company'>
            {(fieldProps: FieldProps) =>
              resourceAutocomplete<ICompany>({
                resourcePath: 'company',
                requestProps: {
                  initialText: t('Creating audit company...'),
                  errorText: t('There was an error creating audit company'),
                  successText: t('Audit company successfully created!'),
                  createRequestFunction: createCompany,
                },
                resourceSortComparator: (a, b) => sortByAlphabeticalOrder(a.name, b.name),
                resourceFilter: (company: ICompany) =>
                  company._id === (user?.company as ICompany)._id ||
                  company._id === currentCustomer?._id ||
                  company.company_type?.includes('inspection_company'),
                modalProps: {
                  form: {
                    initialValues: initialCompany(),
                  },
                  title: t('Create Company'),
                  children: <CompanyForm />,
                },
                renderOption: (props, option) => (
                  <Box component='li' {...props} key={option._id}>
                    <Box>
                      <Typography>{option.name}</Typography>
                      <Typography variant='caption'>Parent: {option.parent ?? 'None'}</Typography>
                    </Box>
                  </Box>
                ),
                autocompleteProps: {
                  sx: {
                    minWidth: 200,
                    my: 1,
                    flex: 1,
                  },
                  disabled: editing,
                  primaryIdentifier: 'name',
                  label: `${t('Audit Company')} *`,
                  ...fieldProps,
                },
              })
            }
          </Field>
          <Field
            name='general_info.contact_info'
            placeholder={t('Contact Info')}
            label={t('Contact Info')}
            value={formik?.values?.general_info?.contact_info}
            onChange={(_: FormEvent, newValue: string) =>
              formik.setFieldValue('general_info.contact_info', newValue)
            }
            options={sortedContactInfoOptions}
            component={Autocomplete}
            disabled={!currentCustomer}
            getOptionLabel={(option: IContactInfo) => option?.tech_focal}
            renderInput={(params: AutocompleteRenderInputParams) => (
              <VinciInput
                {...params}
                name='contact_info'
                label={t('Contact Info')}
                variant='outlined'
                onMouseEnter={() => setShouldRenderReloadContactInfo(true)}
                onMouseLeave={() => setShouldRenderReloadContactInfo(false)}
                InputProps={{
                  ...params.InputProps,
                  ...(currentCustomer &&
                    shouldRenderReloadContactInfo && {
                      endAdornment: (
                        <>
                          <Tooltip title={t('Reload recent contact info')}>
                            <IconButton size='small' onClick={handleReloadContactInfo}>
                              <Icon sx={{ color: theme.palette.primary.main }}>refresh</Icon>
                            </IconButton>
                          </Tooltip>
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }),
                }}
              />
            )}
            sx={{
              my: 1,
              flex: 1,
            }}
          />
        </S.InputGroup>
        <S.SectionDivider />
        <S.InputGroup>
          <Field name='aircraft'>
            {(fieldProps: FieldProps) =>
              resourceAutocomplete<any>({
                resourcePath: 'aircraft',
                requestParams: {
                  limit: 999,
                },
                resourceFilter(item) {
                  return item.aircraft_model?.asset_type?.name == 'Real Estate'
                },
                resourceGetter(data) {
                  if (data instanceof Object && 'docs' in data) {
                    return (data.docs as IAircraft[]) ?? []
                  }

                  return []
                },
                requestProps: {
                  initialText: t('Creating asset...'),
                  errorText: t('There was an error creating asset. Please try again!'),
                  successText: t('Asset successfully created!'),
                  createRequestFunction: undefined,
                },
                onCreate: (initialString) => {
                  setRealStateAssetModal({
                    status: 'creating',
                    initialAsCustom: {
                      city: initialString,
                    },
                  })
                },
                renderOption: (props, option) => {
                  const assetLabel = formatRealStateAddress(
                    option.serial_number,
                    option.aircraft_model?.name ?? '',
                    option.aircraft_model?.manufacturer?.name ?? '',
                  )

                  return (
                    <Box component='li' {...props} key={option._id}>
                      <Box>
                        <Typography>{assetLabel}</Typography>
                        <Typography variant='caption'>
                          Scope: {option?.owner?.name ?? 'None'}
                        </Typography>
                        <Typography variant='caption' ml={3}>
                          Asset Type: {option?.aircraft_model?.asset_type?.name ?? 'None'}
                        </Typography>
                      </Box>
                    </Box>
                  )
                },
                onSelectResource: (resource: IAircraft) => {
                  formik.setFieldValue(
                    editing ? 'general_info.checklist' : 'general_info.checklist_ref',
                    undefined,
                  )
                  if (resource.manufacture_date) {
                    formik.setFieldValue('general_info.yom', resource.manufacture_date)
                    formik.setFieldValue('general_info.dom', resource.manufacture_date)
                  }
                },
                matchValueOnBlur: (value: string, options) =>
                  options.find((a) => {
                    return value ? a.serial_number.includes(value) : false
                  }),
                modalProps: {
                  form: {
                    initialValues: initialCustomAsset(),
                    validationSchema: companyAssetValidationSchema,
                  },
                  title: t('Create Asset'),
                  children: <RealStateAssetForm />,
                },
                autocompleteProps: {
                  sx: {
                    minWidth: 200,
                    my: 1,
                    flex: 1,
                  },
                  primaryIdentifier: 'serial_number',
                  optionLabelGetter: (item: IAircraft) => {
                    if (!item) return ''
                    const assetLabel = formatRealStateAddress(
                      item.serial_number,
                      item.aircraft_model?.name ?? '',
                      item.aircraft_model?.manufacturer?.name ?? '',
                    )

                    return `${assetLabel} (${(item?.owner as ICompany)?.name ?? '-'})`
                  },
                  disabled: !currentCustomer || useSelectedAircraft,
                  label: `${t('Real Estate Asset')} *`,
                  error: formik.touched.aircraft && Boolean(formik.errors.aircraft),
                  helperText: formik.touched.aircraft && formik.errors.aircraft,
                  ...fieldProps,
                },
              })
            }
          </Field>
        </S.InputGroup>
        <S.InputGroup>
          <Field name={editing ? 'general_info.checklist' : 'general_info.checklist_ref'}>
            {(props: FieldProps) =>
              resourceAutocomplete<IInspectionChecklist>({
                resourcePath: 'inspection_checklist',
                requestParams: {
                  lean: 'true',
                },
                requestProps: {
                  initialText: t('Creating checklist...'),
                  errorText: t('There was an error creating checklist. Please try again!'),
                  successText: t('Checklist successfully created!'),
                },
                resourceFilter: (item) => {
                  return (
                    (item.aircraft_model as IAircraftModel)?._id ===
                      formik?.values?.aircraft?.aircraft_model?._id &&
                    (item.customer as ICompany)?._id ===
                      (formik?.values?.general_info?.customer as ICompany)?._id
                  )
                },
                onSelectResource: handleSelectChecklist,
                autocompleteProps: {
                  sx: {
                    minWidth: 200,
                    my: 1,
                    flex: 1,
                  },
                  optionLabelGetter: (item: IInspectionChecklist) => {
                    const customer = item.customer as ICompany
                    if (!customer?.name) return item.description
                    return `${customer.name} - ${item.description}`
                  },
                  disabled: editing || !formik?.values?.aircraft,
                  primaryIdentifier: 'description',
                  label: `${t('Checklist')} *`,
                  error:
                    formik.touched?.general_info?.checklist_ref &&
                    Boolean(formik.errors.general_info?.checklist_ref),
                  helperText:
                    formik.touched?.general_info?.checklist_ref &&
                    formik.errors.general_info?.checklist_ref,
                  ...props,
                },
              })
            }
          </Field>
        </S.InputGroup>
      </S.Section>
      <S.Section>
        <S.InputGroup>
          <Field name='general_info.inspector'>
            {(props: FieldProps) =>
              resourceAutocomplete<IUser>({
                resourcePath: 'user',
                resourceFilter: (user: IUser) =>
                  checkIsSupervisorUp(user) &&
                  ((user?.company as ICompany)?._id === currentCustomer?._id ||
                    (user?.company as ICompany)?._id ===
                      formik?.values?.general_info?.inspection_company?._id),
                resourceSortComparator: (a, b) => sortByAlphabeticalOrder(a.name, b.name),
                requestProps: {
                  initialText: t('Creating user...'),
                  errorText: t('There was an error creating user. Please try again!'),
                  successText: t('User successfully created!'),
                  createRequestFunction: handleCreateUser,
                },
                modalProps: {
                  form: {
                    initialValues: { ...initialUser(), profile: 'inspector' },
                  },
                  title: t('Create User'),
                  children: <UserForm />,
                },
                autocompleteProps: {
                  sx: {
                    minWidth: 200,
                    my: 1,
                    flex: 1,
                  },
                  primaryIdentifier: 'name',
                  label: `${t('Lead Auditor/Supervisor')} *`,
                  error:
                    formik.touched.general_info?.inspector &&
                    Boolean(formik.errors.general_info?.inspector),
                  helperText:
                    formik.touched.general_info?.inspector && formik.errors.general_info?.inspector,
                  ...props,
                },
              })
            }
          </Field>

          <FormControl sx={{ flex: 1, m: 1 }}>
            <InputLabel id='additional-inspectors-label'>{t('Additional Auditors')} *</InputLabel>
            <Select
              labelId='additional-inspectors-label'
              name='general_info.additional_inspectors'
              placeholder={`${t('Additional Auditors')} *`}
              label={`${t('Additional Auditors')} *`}
              value={formik?.values?.general_info?.additional_inspectors ?? []}
              renderValue={(selected) => {
                return selected
                  .map(
                    (inspector_id) =>
                      sortedUsers?.find((i) => i._id === (inspector_id as unknown as string))?.name,
                  )
                  .filter(Boolean)
                  .join(', ')
              }}
              onChange={formik.handleChange}
              sx={{ flex: 1 }}
              multiple
            >
              {sortedUsers
                ?.filter((user) => {
                  return (
                    (user?.company as ICompany)?._id === currentCustomer?._id ||
                    (user?.company as ICompany)?._id ===
                      formik?.values?.general_info?.inspection_company?._id
                  )
                })
                .map((inspector) => {
                  return (
                    <MenuItem key={inspector.name} value={inspector._id}>
                      <Checkbox
                        size='small'
                        checked={Boolean(
                          formik.values.general_info?.additional_inspectors?.find(
                            (i) => (i as unknown as string) === inspector._id,
                          ),
                        )}
                      />
                      {inspector.name}
                    </MenuItem>
                  )
                })}
            </Select>
          </FormControl>
        </S.InputGroup>
        <S.InputGroup>
          <CountrySelect
            sx={{
              flex: 1,
            }}
            value={formik.values.general_info?.country}
            label={`${t('Country')}`}
            onChange={(newValue) => {
              if (!newValue) {
                return formik.setValues({
                  ...formik.values,
                  general_info: {
                    ...formik.values.general_info,
                    country: null,
                    region: null,
                  },
                })
              }

              const countryContinent = getContinentByCountryName(newValue)
              if (countryContinent) {
                formik.setValues({
                  ...formik.values,
                  general_info: {
                    ...formik.values.general_info,
                    country: newValue,
                    region: countryContinent,
                  },
                })
              } else {
                formik.setFieldValue('general_info.country', newValue, true)
              }
            }}
          />
          <S.FastField
            component={FormikVinciInput}
            name='general_info.region'
            label={t('Region')}
            disabled
            InputLabelProps={{ shrink: true }}
          />
          <S.FastField
            component={DatePicker}
            name='general_info.due_date'
            label={t('Due Date')}
            openTo='year'
            views={['year', 'month', 'day']}
          />
        </S.InputGroup>
        <S.SectionDivider sx={{ my: 3 }} />
        <Grid container>
          {realStateExtraFields.map((field) => {
            return (
              <Grid key={field.field} item xs={12} md={6} px={1} py={2}>
                <CustomField field={field} />
              </Grid>
            )
          })}
        </Grid>
      </S.Section>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          width: '100%',
        }}
      >
        <VinciButton color='primary' onClick={() => navigate(-1)} size='large' variant='outlined'>
          {t('Back')}
        </VinciButton>
        <VinciButton
          color='primary'
          size='large'
          onClick={!editing ? handleClick : handleSave}
          disabled={submitButtonDisabled}
        >
          {editing ? t('Save') : t('Create')}
        </VinciButton>
        <CreateAuditMenu
          onCreate={onCreate}
          onSave={onSave}
          editing={editing}
          anchorEl={anchorEl}
          isCreateAuditMenuOpen={isCreateAuditMenuOpen}
          handleClose={handleClose}
        />
      </Box>
    </>
  )
}
